import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Tag, X } from 'lucide-react';
import { api, formatMoney } from '../api';
import { useAuth } from '../auth';
import { plans } from '../data';

export function Pricing() {
  const [annual, setAnnual] = useState(true);
  const [family, setFamily] = useState<'All' | 'Media + CDN' | 'Logs' | 'All in one'>('All');
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [purchasesEnabled, setPurchasesEnabled] = useState(true);
  const [support, setSupport] = useState<{
    slug: string;
    display_name: string;
    url: string;
    locked?: boolean;
  } | null>(null);
  const [changing, setChanging] = useState(false);
  const [supportDraft, setSupportDraft] = useState('');
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    void api<{ purchases_enabled?: boolean }>('/auth/security-config')
      .then((result) => setPurchasesEnabled(result.purchases_enabled !== false))
      .catch(() => undefined);
  }, []);
  const { loading: authLoading } = useAuth();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ref_notice') === 'self_referral') {
      setError('You cannot use your own referral link or refer accounts from the same network/IP.');
      params.delete('ref_notice');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    }
  }, []);
  useEffect(() => {
    if (authLoading) return;
    const path = user ? '/affiliates/support' : '/affiliates/pending';
    void api<{ referral?: { slug: string; display_name: string; url: string; locked?: boolean } | null; creator?: { slug: string; display_name: string; url: string } | null }>(path)
      .then((result) => setSupport(result.referral ?? result.creator ?? null))
      .catch(() => undefined);
  }, [user, authLoading]);
  const checkout = async (code: string) => {
    if (code !== 'free' && !purchasesEnabled) {
      setError('Plan purchases are temporarily unavailable.');
      return;
    }
    if (code === 'free') {
      navigate(user ? '/dashboard/billing' : '/signup');
      return;
    }
    if (!user) {
      navigate(`/signup?plan=${code}&interval=${annual ? 'annual' : 'monthly'}`);
      return;
    }
    setLoading(code);
    setError('');
    setNotice('');
    try {
      if (user.has_subscription && user.subscription_status !== 'none' && user.subscription_status !== 'canceled') {
        const result = await api<{ scheduled: boolean }>('/billing/change-plan', {
          method: 'POST',
          body: JSON.stringify({ plan_code: code }),
        });
        setNotice(
          result.scheduled
            ? 'Your downgrade is scheduled for the next renewal.'
            : 'Your upgraded plan is active now.',
        );
        await refresh();
        return;
      }
      const result = await api<{ url: string }>('/billing/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({ plan_code: code, interval: annual ? 'annual' : 'monthly' }),
      });
      window.location.assign(result.url);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Checkout could not be opened. Please try again.',
      );
    } finally {
      setLoading('');
    }
  };
  return (
    <div className="pricing-page">
      <section className="page-intro shell">
        <p>Simple capacity pricing</p>
        <h1>
          Pay for the space.
          <br />
          Use the network freely.
        </h1>
        <p className="pricing-enterprise-note">
          Psst. Looking for enterprise pricing? Don’t bother hunting for a special price list.
          Choose the plan that fits below. Individuals and companies get the same infrastructure,
          support and service quality. (:
        </p>
        <div className="pricing-controls">
          <div className="segmented" role="radiogroup" aria-label="Billing interval">
            <button
              className={!annual ? 'is-active' : ''}
              onClick={() => setAnnual(false)}
              role="radio"
              aria-checked={!annual}
            >
              Monthly
            </button>
            <button
              className={annual ? 'is-active' : ''}
              onClick={() => setAnnual(true)}
              role="radio"
              aria-checked={annual}
            >
              Annual <span>1 month free</span>
            </button>
          </div>
          <div className="filter-tabs">
            {(['All', 'Media + CDN', 'Logs', 'All in one'] as const).map((item) => (
              <button
                key={item}
                className={family === item ? 'is-active' : ''}
                onClick={() => setFamily(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <p className="pricing-tax-note">
          Prices exclude VAT. Applicable taxes are calculated before payment.
        </p>
        {user ? (
          <div className="pricing-referral">
            {support ? (
              <div className="referral-banner">
                <div className="referral-banner__content">
                  <div className="referral-banner__tag">
                    <Tag size={13} />
                    <span>Creator referral</span>
                  </div>
                  <div className="referral-banner__info">
                    <p className="referral-banner__title">
                      Supporting <strong>{support.display_name}</strong> <span className="referral-badge">@{support.slug}</span>
                    </p>
                    <p className="referral-banner__subtitle">
                      {!support.locked
                        ? 'Any eligible discount under this creator’s agreement is shown at checkout. Your first payment locks this creator to your account.'
                        : 'Your account is permanently linked to this creator for all subscription payments.'}
                    </p>
                  </div>
                </div>
                {!support.locked && !changing && (
                  <button
                    type="button"
                    className="referral-banner__action"
                    onClick={() => { setChanging(true); setSupportDraft(''); }}
                  >
                    Change
                  </button>
                )}
                {changing && (
                  <form
                    className="referral-banner__form"
                    onSubmit={async (event) => {
                      event.preventDefault();
                      setError('');
                      try {
                        const result = await api<{ referral: { slug: string; display_name: string; url: string; locked?: boolean } }>(
                          '/affiliates/support',
                          { method: 'POST', body: JSON.stringify({ slug: supportDraft }) },
                        );
                        setSupport(result.referral);
                        setChanging(false);
                        setNotice('Referral updated.');
                      } catch (failure) {
                        setError(failure instanceof Error ? failure.message : 'That referral ID could not be saved.');
                      }
                    }}
                  >
                    <input
                      value={supportDraft}
                      onChange={(event) => setSupportDraft(event.target.value)}
                      placeholder="Enter new creator ID"
                      minLength={3}
                      required
                    />
                    <button className="button button--small" type="submit">Save</button>
                    <button className="button button--quiet button--small" type="button" onClick={() => setChanging(false)}>Cancel</button>
                  </form>
                )}
              </div>
            ) : (
              changing ? (
                <form
                  className="referral-inline-form"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    setError('');
                    try {
                      const result = await api<{ referral: { slug: string; display_name: string; url: string; locked?: boolean } }>(
                        '/affiliates/support',
                        { method: 'POST', body: JSON.stringify({ slug: supportDraft }) },
                      );
                      setSupport(result.referral);
                      setChanging(false);
                      setNotice('This account now supports that creator.');
                    } catch (failure) {
                      setError(failure instanceof Error ? failure.message : 'That referral ID could not be saved.');
                    }
                  }}
                >
                  <label>
                    <span>Creator referral ID</span>
                    <div className="referral-inline-row">
                      <input
                        value={supportDraft}
                        onChange={(event) => setSupportDraft(event.target.value)}
                        placeholder="e.g. alberteinstein"
                        minLength={3}
                        required
                      />
                      <button className="button button--small" type="submit">Apply</button>
                      <button className="button button--quiet button--small" type="button" onClick={() => setChanging(false)}>Cancel</button>
                    </div>
                  </label>
                </form>
              ) : (
                <button className="button button--quiet button--small referral-prompt-btn" onClick={() => setChanging(true)}>
                  <Tag size={13} />
                  Have a creator referral code?
                </button>
              )
            )}
          </div>
        ) : support ? (
          <div className="pricing-referral">
            <div className="referral-banner">
              <div className="referral-banner__content">
                <div className="referral-banner__tag">
                  <Tag size={13} />
                  <span>Creator referral</span>
                </div>
                <div className="referral-banner__info">
                  <p className="referral-banner__title">
                    Join through <strong>{support.display_name}</strong> <span className="referral-badge">@{support.slug}</span>
                  </p>
                  <p className="referral-banner__subtitle">
                    Join through this creator. Any eligible customer discount will be shown at checkout.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </section>
      {error && (
        <div className="checkout-error shell" role="alert">
          <strong>Checkout could not be opened</strong>
          <span>{error}</span>
          <button onClick={() => setError('')} aria-label="Dismiss checkout error">
            <X />
          </button>
        </div>
      )}
      {notice && (
        <div className="checkout-notice shell" role="status">
          <Check />
          <span>{notice}</span>
        </div>
      )}
      <section className="shell pricing-catalog">
        {plans
          .filter((plan) => family === 'All' || plan.family === family)
          .slice()
          .sort((left, right) => (annual ? left.annual : left.monthly) - (annual ? right.annual : right.monthly))
          .map((plan) => (
            <article
              className={
                plan.code === 'community_pro' ? 'price-card price-card--featured' : 'price-card'
              }
              key={plan.code}
            >
              <div>
                <small>{plan.family}</small>
                {plan.code === 'community_pro' && <span className="plan-signal">Recommended</span>}
              </div>
              <h2>{plan.name}</h2>
              <p>{plan.note}</p>
              <div className="price">
                {annual && plan.monthly > 0 && (
                  <del aria-label={`${formatMoney(plan.monthly * 12)} standard annual price`}>
                    {formatMoney(plan.monthly * 12)}
                  </del>
                )}
                <strong>{formatMoney(annual ? plan.annual : plan.monthly)}</strong>
                <span>/{annual ? 'year' : 'month'}</span>
              </div>
              {plan.monthly > 0 && (
                <small style={{ display: 'block', marginTop: '-4px', marginBottom: '8px', fontSize: '11px', color: 'var(--color-muted)' }}>
                  {(() => {
                    const net = annual ? plan.annual : plan.monthly;
                    const withVat = Math.round(net * 1.21);
                    return `Total: ${formatMoney(withVat)} with 21% VAT`;
                  })()}
                </small>
              )}
              <ul>
                {plan.storage && (
                  <li>
                    <Check />
                    {plan.storage} CDN storage
                  </li>
                )}
                {plan.logs && (
                  <li>
                    <Check />
                    {plan.logs} logs
                  </li>
                )}
                <li>
                  <Check />
                  100% Unmetered Bandwidth (Free)
                </li>
                <li>
                  <Check />
                  {plan.family === "Logs" ? (
                    <span>Unlimited queries (Not CDN)</span>
                  ) : plan.fairUse ? (
                    <span>
                      Unlimited CDN Requests (<a href="/fair-use" style={{ color: "inherit", textDecoration: "underline" }} target="_blank" rel="noreferrer">Under Fair Use</a>)
                    </span>
                  ) : (
                    <span>{plan.requests} CDN Requests</span>
                  )}
                </li>
                <li>
                  <Check />
                  REST and S3-compatible APIs
                </li>
                <li>
                  <Check />
                  The same edge network and support
                </li>
                {plan.logs && (
                  <li>
                    <Check />
                    Infinite log retention
                  </li>
                )}
              </ul>
              <button
                className="button button--full"
                data-state={loading === plan.code ? 'loading' : undefined}
                disabled={
                  Boolean(loading) ||
                  user?.plan_code === plan.code ||
                  user?.pending_plan_code === plan.code ||
                  (plan.code !== 'free' && !purchasesEnabled)
                }
                onClick={() => checkout(plan.code)}
              >
                {user?.pending_plan_code === plan.code
                  ? 'Scheduled plan'
                  : user?.plan_code === plan.code
                    ? 'Current plan'
                    : loading === plan.code
                      ? 'Updating plan…'
                      : plan.code !== 'free' && !purchasesEnabled
                        ? 'Purchases paused'
                      : plan.code === 'free'
                        ? user
                          ? 'Manage free plan'
                          : 'Start free'
                        : user && user.has_subscription && user.subscription_status !== 'none'
                          ? `Change to ${plan.name}`
                          : `Choose ${plan.name}`}
              </button>
            </article>
          ))}
      </section>
      <CustomPlanBuilder user={user} purchasesEnabled={purchasesEnabled} annual={annual} support={support} />
      <section className="pricing-faq shell">
        <div>
          <h2>Questions worth answering.</h2>
          <p>No hidden request line items. No bandwidth calculator to babysit.</p>
        </div>
        <dl>
          <div>
            <dt>What happens when I reach the storage limit?</dt>
            <dd>
              New uploads stop cleanly. Existing files stay online. Automatic removal only applies
              after a canceled, unpaid, or downgraded plan exceeds its allowance and the seven-day
              grace period has ended.
            </dd>
          </div>
          <div>
            <dt>Does infinite retention mean logs can never be deleted?</dt>
            <dd>
              No. It means BCKT does not force a 30 or 90 day expiration. You can delete data or set
              a custom retention window whenever you need one.
            </dd>
          </div>
          <div>
            <dt>Can I change plans later?</dt>
            <dd>
              Yes. Upgrades, downgrades, invoices, and payment methods are handled in the Stripe
              customer portal.
            </dd>
          </div>
          <div>
            <dt>Are annual plans paid upfront?</dt>
            <dd>
              Yes. Annual pricing covers twelve months for the cost of eleven monthly payments.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
export function CustomPlanBuilder({
  user,
  purchasesEnabled,
  annual: initialAnnual = true,
  support,
}: {
  user: any;
  purchasesEnabled: boolean;
  annual?: boolean;
  support: any;
}) {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(initialAnnual);
  const [cdnGb, setCdnGb] = useState(250);
  const [logGb, setLogGb] = useState(5);
  const [requestsMillions, setRequestsMillions] = useState(1);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState("");

  const cdnMonthlyCents = Math.round(cdnGb * 1.6);
  const logMonthlyCents = Math.round(Math.max(0, logGb - 5) * 1.2);
  const reqMonthlyCents = Math.round(requestsMillions * 50);
  const baseMonthlyNet = Math.max(499, cdnMonthlyCents + logMonthlyCents + reqMonthlyCents);

  const discountedMonthlyNet = support && !support.locked ? Math.round(baseMonthlyNet * 0.9) : baseMonthlyNet;
  const annualNet = Math.round(baseMonthlyNet * 11);
  const finalNet = annual ? annualNet : discountedMonthlyNet;
  const withVat = Math.round(finalNet * 1.21);

  const handleSubscribe = async () => {
    if (!purchasesEnabled) {
      setError("Purchases are temporarily unavailable.");
      return;
    }
    if (!user) {
      navigate(`/signup?custom=true&cdn=${cdnGb}&log=${logGb}&req=${requestsMillions}&interval=${annual ? "annual" : "monthly"}`);
      return;
    }
    setSubscribing(true);
    setError("");
    try {
      const res = await api<{ success: boolean; url: string }>("/billing/create-custom-checkout-session", {
        method: "POST",
        body: JSON.stringify({
          storage_gb: Number(cdnGb) || 250,
          log_gb: Number(logGb) || 5,
          monthly_requests_millions: Number(requestsMillions) || 1,
          interval: annual ? "annual" : "monthly",
        }),
      });
      if (res.url) window.location.href = res.url;
      else throw new Error("No checkout URL returned.");
    } catch (err: any) {
      setError(err.message || "Failed to initialize custom checkout.");
      setSubscribing(false);
    }
  };

  return (
    <section className="shell" style={{ margin: "48px auto" }}>
      <div
        className="dash-card"
        style={{
          padding: "36px",
          borderRadius: "16px",
          background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "32px" }}>
          <div style={{ maxWidth: "640px" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Custom Tailored Plan
            </span>
            <h2 style={{ fontSize: "28px", marginTop: "6px", marginBottom: "8px" }}>Build Your Exact Workspace Plan</h2>
            <p style={{ color: "var(--color-muted)", fontSize: "14px", lineHeight: 1.6 }}>
              Configure your exact quota with guaranteed zero bandwidth fees. Type any value or drag the sliders.
            </p>
          </div>

          <div className="segmented" role="radiogroup" aria-label="Custom billing interval" style={{ alignSelf: "center" }}>
            <button
              type="button"
              className={!annual ? "is-active" : ""}
              onClick={() => setAnnual(false)}
              role="radio"
              aria-checked={!annual}
            >
              Monthly
            </button>
            <button
              type="button"
              className={annual ? "is-active" : ""}
              onClick={() => setAnnual(true)}
              role="radio"
              aria-checked={annual}
            >
              Annual <span style={{ color: "var(--color-accent)", fontSize: "11px", fontWeight: 650 }}>1 mo free</span>
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "32px", alignItems: "start" }}>
          <div style={{ display: "grid", gap: "24px" }}>
            {/* CDN Storage Input & Slider */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "14px", fontWeight: 550 }}>CDN Media Storage (GB)</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="number"
                    min={250}
                    max={200000}
                    step={50}
                    value={cdnGb}
                    onChange={(e) => {
                      const val = Math.max(250, Math.min(200000, Number(e.target.value) || 250));
                      setCdnGb(val);
                    }}
                    style={{
                      width: "110px",
                      padding: "4px 8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(0,0,0,0.3)",
                      color: "var(--color-ink)",
                      textAlign: "right",
                    }}
                  />
                  <span style={{ fontSize: "13px", color: "var(--color-muted)", minWidth: "50px" }}>
                    {cdnGb >= 1000 ? `(${(cdnGb / 1000).toFixed(1)} TB)` : "GB"}
                  </span>
                </div>
              </div>
              <input
                type="range"
                min={250}
                max={200000}
                step={250}
                value={cdnGb}
                onChange={(e) => setCdnGb(Number(e.target.value))}
                style={{ width: "100%", accentColor: "var(--color-accent)", cursor: "pointer" }}
              />
              <small style={{ color: "var(--color-muted)", fontSize: "11px", display: "flex", justifyContent: "space-between" }}>
                <span>250 GB (Min)</span>
                <span>200 TB (Max)</span>
              </small>
            </div>

            {/* Log Storage Input & Slider */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "14px", fontWeight: 550 }}>Log Storage (GB)</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="number"
                    min={5}
                    max={50000}
                    step={5}
                    value={logGb}
                    onChange={(e) => {
                      const val = Math.max(5, Math.min(50000, Number(e.target.value) || 5));
                      setLogGb(val);
                    }}
                    style={{
                      width: "110px",
                      padding: "4px 8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(0,0,0,0.3)",
                      color: "var(--color-ink)",
                      textAlign: "right",
                    }}
                  />
                  <span style={{ fontSize: "13px", color: "var(--color-muted)", minWidth: "50px" }}>
                    {logGb >= 1000 ? `(${(logGb / 1000).toFixed(1)} TB)` : "GB"}
                  </span>
                </div>
              </div>
              <input
                type="range"
                min={5}
                max={50000}
                step={5}
                value={logGb}
                onChange={(e) => setLogGb(Number(e.target.value))}
                style={{ width: "100%", accentColor: "var(--color-accent)", cursor: "pointer" }}
              />
              <small style={{ color: "var(--color-muted)", fontSize: "11px", display: "flex", justifyContent: "space-between" }}>
                <span>5 GB included</span>
                <span>50 TB (Max)</span>
              </small>
            </div>

            {/* CDN Requests Input & Slider */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "14px", fontWeight: 550 }}>Monthly CDN Requests (Million)</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="number"
                    min={1}
                    max={2000}
                    step={1}
                    value={requestsMillions}
                    onChange={(e) => {
                      const val = Math.max(1, Math.min(2000, Number(e.target.value) || 1));
                      setRequestsMillions(val);
                    }}
                    style={{
                      width: "110px",
                      padding: "4px 8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(0,0,0,0.3)",
                      color: "#4ade80",
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  />
                  <span style={{ fontSize: "13px", color: "var(--color-muted)", minWidth: "50px" }}>
                    {requestsMillions >= 1000 ? `(${(requestsMillions / 1000).toFixed(1)}B)` : "M"}
                  </span>
                </div>
              </div>
              <input
                type="range"
                min={1}
                max={2000}
                step={1}
                value={requestsMillions}
                onChange={(e) => setRequestsMillions(Number(e.target.value))}
                style={{ width: "100%", accentColor: "#4ade80", cursor: "pointer" }}
              />
              <small style={{ color: "var(--color-muted)", fontSize: "11px", display: "flex", justifyContent: "space-between" }}>
                <span>1M requests (Min)</span>
                <span>€0.50 per 1M requests</span>
                <span>2,000 Million / 2B (Max)</span>
              </small>
            </div>
          </div>

          {/* Pricing Summary Box */}
          <div
            style={{
              padding: "24px",
              borderRadius: "12px",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.08)",
              display: "grid",
              gap: "16px",
            }}
          >
            <div>
              <span style={{ fontSize: "12px", color: "var(--color-muted)", textTransform: "uppercase" }}>Custom Plan Quote</span>
              <div style={{ fontSize: "32px", fontWeight: 650, marginTop: "4px" }}>
                {formatMoney(finalNet)}
                <span style={{ fontSize: "14px", fontWeight: 400, color: "var(--color-muted)" }}>
                  /{annual ? "year" : "month"}
                </span>
              </div>
              <small style={{ color: "var(--color-muted)", fontSize: "12px", display: "block", marginTop: "4px" }}>
                Total: <strong>{formatMoney(withVat)}</strong> incl. 21% VAT
              </small>
            </div>

            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "8px", fontSize: "13px" }}>
              <li style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Check style={{ width: "15px", color: "#4ade80" }} />
                <strong>{cdnGb >= 1000 ? (cdnGb / 1000).toFixed(1) + " TB" : cdnGb + " GB"}</strong> global CDN object storage
              </li>
              <li style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Check style={{ width: "15px", color: "#4ade80" }} />
                <strong>{logGb} GB</strong> searchable logs & analytics
              </li>
              <li style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Check style={{ width: "15px", color: "#4ade80" }} />
                <strong>{requestsMillions} Million</strong> CDN delivery requests/mo
              </li>
              <li style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Check style={{ width: "15px", color: "#4ade80" }} />
                100% Unmetered Bandwidth (Always free)
              </li>
            </ul>

            {error && <p className="form-error" style={{ margin: 0, fontSize: "12px" }}>{error}</p>}

            <button
              className="button button--accent button--full"
              style={{ padding: "12px", fontSize: "14px", fontWeight: 600 }}
              disabled={subscribing}
              onClick={handleSubscribe}
            >
              {subscribing ? "Connecting to checkout..." : user?.plan_code === "custom" ? "Update Custom Plan" : "Subscribe to Custom Plan"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
